import { View, Text } from "@src/components";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { Icon, type IconNameType } from "@src/components/Icon";
import { IconButton } from "@src/components/IconButton";
import { Button } from "@src/components/Button";
import { memo, useMemo, type FC } from "react";
import { PressableScale } from "pressto";
import { ControlRow } from "../ControlRow";
import { Animated, PulseEffect, useAnimatedColorTransition } from "@visioni/a3s-ui/animations";
import { FeatureGate } from "@src/services/feature-flags/FeatureGate";
import { FEATURE_FLAGS } from "@src/services/feature-flags/feature-flags";

type ArmDisarmControlProps = {
  isArmed: boolean;
  isActivated: boolean;
  onArmDisarm: () => void;
  onAdjustOverride?: () => void;
  onResumeSchedule?: () => void;
  onSilenceAlarm: () => void;
  statusText?: string;
  isSilenced: boolean;
  silencedMinutes: number;
  hasActiveOverride?: boolean;
  overrideAction?: "armed" | "disarmed";
};

const ICON_SIZE = 16;

export const ArmDisarmControl: FC<ArmDisarmControlProps> = memo(
  ({
    isArmed,
    isActivated,
    onArmDisarm,
    onAdjustOverride,
    onResumeSchedule,
    onSilenceAlarm,
    statusText = "Scheduled",
    isSilenced,
    silencedMinutes,
    hasActiveOverride = false,
    overrideAction,
  }) => {
    const { theme } = useUnistyles();

    // 0 = disarmed, 1 = armed, 2 = activated
    const iconColorState =
      isActivated ? 2
      : isArmed ? 1
      : 0;

    const icon = useMemo(() => {
      if (isActivated) {
        return {
          name: "warningOutline" as IconNameType,
          backgroundColor: theme.colors.status.error,
          borderColor: theme.colors.status.error,
        };
      }
      if (!isArmed) {
        return {
          name: "lockOpen" as IconNameType,
          backgroundColor: theme.colors.warning[600],
          borderColor: theme.colors.warning[400],
        };
      }
      return {
        name: "lockClosed" as IconNameType,
        backgroundColor: theme.colors.purple[600],
        borderColor: theme.colors.purple[200],
      };
    }, [isArmed, isActivated, theme]);

    const animatedIconButtonStyle = useAnimatedColorTransition(iconColorState, {
      backgroundColor: [theme.colors.warning[600], theme.colors.purple[600], theme.colors.status.error],
      borderColor: [theme.colors.warning[400], theme.colors.purple[200], theme.colors.status.error],
    });

    const textControl = useMemo(() => {
      if (isActivated) {
        return "Alarm activated";
      }
      if (isArmed) {
        return "Armed";
      }
      return "Disarmed";
    }, [isArmed, isActivated]);

    // 0 = default, 1 = warning (disarmed override), 2 = purple (armed override), 3 = danger (activated)
    const containerBorderState =
      isActivated ? 3
      : hasActiveOverride && overrideAction === "armed" ? 2
      : hasActiveOverride ? 1
      : 0;

    const animatedContainerStyle = useAnimatedColorTransition(containerBorderState, {
      borderColor: [theme.colors.border, theme.colors.warning[500], theme.colors.purple[500], theme.colors.danger[500]],
    });

    return (
      <Animated.View style={[styles.container, animatedContainerStyle]}>
        <ControlRow
          icon={
            <View style={{ width: 32, height: 32, borderRadius: 999 }}>
              {isActivated &&
                [0, 400].map((delay) => (
                  <PulseEffect
                    key={`pulse-activated-${String(delay)}`}
                    variant="ripple"
                    color={icon.borderColor}
                    delay={delay}
                    size={32}
                  />
                ))}
              {isArmed && !isActivated && <PulseEffect variant="breathe" color={icon.borderColor} size={32} />}
              <IconButton
                Icon={() => <Icon name={icon.name} size={ICON_SIZE} color={theme.colors.white.default} />}
                size="sm"
                title="Lock"
                buttonStyle={{
                  backgroundColor: icon.backgroundColor,
                  borderColor: icon.borderColor,
                  borderWidth: 2,
                }}
                animatedStyle={animatedIconButtonStyle}
              />
            </View>
          }
          title={textControl}
          subtitle={!isActivated ? statusText : undefined}
          titleTextStyle="md/regular"
          subtitleTextStyle="xxs/regular"
          action={
            <FeatureGate flag={FEATURE_FLAGS.CAMERA_ALARM_OVERRIDE}>
              {hasActiveOverride && onResumeSchedule ?
                <Button label="Resume Schedule" onPress={onResumeSchedule} variant="primary" />
              : <Button label={isArmed ? "Disarm" : "Arm"} onPress={onArmDisarm} variant="primary" />}
            </FeatureGate>
          }
        />
        <FeatureGate flag={FEATURE_FLAGS.CAMERA_ALARM_SILENCE}>
          {isActivated && (
            <View mt="3xl">
              <PressableScale
                style={[
                  styles.silenceButton,
                  {
                    backgroundColor: theme.colors.status.error,
                    borderColor: theme.colors.status.error,
                  },
                  isSilenced && styles.silenceButtonActive,
                ]}
                onPress={onSilenceAlarm}
              >
                <View style={styles.silenceButtonContent}>
                  <Icon name="alarmPause" size={ICON_SIZE} color={theme.colors.white.default} />
                  <Text textStyle="sm/regular" style={{ color: theme.colors.white.default }}>
                    Silence alarm
                  </Text>
                </View>
                {isSilenced && (
                  <View>
                    <Text textStyle="xs/regular" style={{ color: theme.colors.white.default }}>
                      Silenced {silencedMinutes} mins
                    </Text>
                  </View>
                )}
              </PressableScale>
            </View>
          )}
        </FeatureGate>
      </Animated.View>
    );
  },
);

const styles = StyleSheet.create((theme: any) => ({
  container: {
    backgroundColor: theme.colors.surface,
    borderColor: theme.colors.border,
    borderWidth: 1,
    borderRadius: theme.spacing.lg,
    paddingVertical: theme.spacing.lg,
    paddingHorizontal: theme.spacing["3xl"],
    alignSelf: "stretch",
  },
  silenceButton: {
    paddingHorizontal: theme.spacing["2xl"],
    paddingVertical: theme.spacing.md,
    borderRadius: 8,
    borderCurve: "continuous",
    minWidth: 80,
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
    backgroundColor: theme.colors.foreground,
    borderColor: theme.colors.border,
    borderWidth: 1,
  },
  silenceButtonContent: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.md,
  },
  silenceButtonActive: {
    justifyContent: "space-between",
  },
  activated: {
    borderColor: theme.colors.danger[500],
    borderWidth: 1,
  },
}));
